import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid, Typography, CircularProgress } from "@mui/material";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip as RTooltip,
} from "recharts";
import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import MiniStat from "../../components/MiniStat";
import { tokens } from "../../theme";
import { endPoints } from "../../constant/Environment";
import { getAllData } from "../../Utility/API";

const mono = '"IBM Plex Mono", monospace';

const num = (v) => Number(v || 0);
const pct = (part, whole) => (whole > 0 ? (part / whole) * 100 : 0);

// The five events the CoinUI reports. Push events stay at zero until the
// service worker / push subscribe flow ships.
const EVENT_SERIES = [
  { key: "pwa_install_prompt_shown", label: "Prompt shown", color: tokens.blue },
  { key: "pwa_install_clicked", label: "Install clicked", color: tokens.primary },
  { key: "pwa_standalone_launch", label: "Standalone launch", color: tokens.secondary },
  { key: "push_permission_granted", label: "Push granted", color: tokens.accent },
  { key: "push_subscribed", label: "Push subscribed", color: tokens.pink },
];

export default function PwaStats() {
  const [loading, setLoading] = useState(true);
  const [funnel, setFunnel] = useState({});
  const [daily, setDaily] = useState([]);
  const [platforms, setPlatforms] = useState([]);

  useEffect(() => {
    let active = true;
    const load = async () => {
      setLoading(true);
      try {
        const [funnelRes, dailyRes, platRes] = await Promise.all([
          getAllData(endPoints.api.PWA_REPORT_FUNNEL),
          getAllData(endPoints.api.PWA_REPORT_DAILY),
          getAllData(endPoints.api.PWA_REPORT_PLATFORMS),
        ]);
        if (!active) return;
        if (funnelRes?.status === "success") setFunnel(funnelRes.data || {});
        if (dailyRes?.status === "success") setDaily(dailyRes.data || []);
        if (platRes?.status === "success") setPlatforms(platRes.data || []);
      } catch (e) {
        console.error("Failed to load PWA reports:", e);
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
  }, []);

  const registrations = num(funnel?.registrations);
  const installs = num(funnel?.installs);
  const pushSubscribers = num(funnel?.pushSubscribers);
  const activeInstalls = num(funnel?.activeInstalls);

  // registrations -> installs -> push subscribers -> active (the sheet's funnel)
  const stages = [
    { label: "Registrations", value: registrations, color: tokens.blue },
    { label: "PWA installs", value: installs, color: tokens.primary },
    { label: "Push subscribers", value: pushSubscribers, color: tokens.secondary },
    { label: "Active installs (30d)", value: activeInstalls, color: tokens.accent },
  ];

  // Pivot [{day, event_name, total}] into one row per day with a column per event.
  const chartData = useMemo(() => {
    const byDay = {};
    (daily || []).forEach((row) => {
      const day = String(row?.day || "").slice(0, 10);
      if (!day) return;
      if (!byDay[day]) byDay[day] = { day };
      byDay[day][row?.event_name] = num(row?.total);
    });
    return Object.values(byDay).sort((a, b) => (a.day < b.day ? -1 : 1));
  }, [daily]);

  return (
    <Box>
      <PageHeader
        title="PWA Stats"
        crumb="Monitor / PWA Stats"
        subtitle="Install funnel · registrations → installs → push subscribers → active"
      />

      <Grid container spacing={1.75} sx={{ mb: 1.75 }}>
        <Grid item xs={6} md={3}>
          <MiniStat label="Registrations" value={registrations.toLocaleString()} color={tokens.blue} />
        </Grid>
        <Grid item xs={6} md={3}>
          <MiniStat
            label="PWA installs"
            value={installs.toLocaleString()}
            note={`${pct(installs, registrations).toFixed(1)}% of registrations`}
            color={tokens.primary}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MiniStat
            label="Push subscribers"
            value={pushSubscribers.toLocaleString()}
            note={`${pct(pushSubscribers, installs).toFixed(1)}% of installs`}
            color={tokens.secondary}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MiniStat
            label="Active installs (30d)"
            value={activeInstalls.toLocaleString()}
            note={`${pct(activeInstalls, installs).toFixed(1)}% of installs`}
            color={tokens.accent}
          />
        </Grid>
      </Grid>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <Grid container spacing={1.75}>
          <Grid item xs={12} lg={5}>
            <SectionCard title="Install funnel" subtitle="Distinct authenticated users per stage">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {stages.map((s, i) => {
                  const width = pct(s.value, registrations);
                  const prev = i === 0 ? null : stages[i - 1];
                  return (
                    <Box key={s.label}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.6 }}>
                        <Typography sx={{ fontSize: 12.5, color: tokens.text }}>{s.label}</Typography>
                        <Typography sx={{ fontFamily: mono, fontSize: 12.5, color: tokens.text }}>
                          {s.value.toLocaleString()}
                          {prev && (
                            <Box component="span" sx={{ color: tokens.textDim, ml: 1 }}>
                              {pct(s.value, prev.value).toFixed(1)}%
                            </Box>
                          )}
                        </Typography>
                      </Box>
                      <Box sx={{ height: 10, borderRadius: 5, bgcolor: tokens.surface2, overflow: "hidden" }}>
                        <Box sx={{ height: "100%", width: `${Math.max(width, s.value > 0 ? 2 : 0)}%`, bgcolor: s.color }} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>

              <Box sx={{ mt: 2.5, pt: 2, borderTop: `1px solid ${tokens.border}` }}>
                <Typography sx={{ fontSize: 11, color: tokens.textDim, textTransform: "uppercase", letterSpacing: 0.5, mb: 1 }}>
                  Prompt performance
                </Typography>
                <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                  <Box>
                    <Typography sx={{ fontFamily: mono, fontSize: 18 }}>{num(funnel?.promptShown).toLocaleString()}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: tokens.textDim }}>Prompt shown</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: mono, fontSize: 18 }}>{num(funnel?.installClicked).toLocaleString()}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: tokens.textDim }}>Install clicked</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: mono, fontSize: 18, color: tokens.secondary }}>
                      {pct(num(funnel?.installClicked), num(funnel?.promptShown)).toFixed(1)}%
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: tokens.textDim }}>Click-through</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontFamily: mono, fontSize: 18 }}>{num(funnel?.installedDevices).toLocaleString()}</Typography>
                    <Typography sx={{ fontSize: 11.5, color: tokens.textDim }}>Installed devices</Typography>
                  </Box>
                </Box>
              </Box>
            </SectionCard>
          </Grid>

          <Grid item xs={12} lg={7}>
            <SectionCard title="Events over time" subtitle="Last 30 days">
              {chartData.length === 0 ? (
                <Typography variant="caption" color="text.secondary">No PWA events recorded yet.</Typography>
              ) : (
                <Box sx={{ height: 300 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ left: -18, right: 8, top: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={tokens.border} vertical={false} />
                      <XAxis dataKey="day" stroke={tokens.textDim} fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis stroke={tokens.textDim} fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                      <RTooltip contentStyle={{ background: tokens.surface2, border: `1px solid ${tokens.border}`, borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {EVENT_SERIES.map((s) => (
                        <Line key={s.key} type="monotone" dataKey={s.key} name={s.label} stroke={s.color} strokeWidth={2} dot={false} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </SectionCard>
          </Grid>

          <Grid item xs={12}>
            <SectionCard title="Installs by platform" subtitle="Devices that launched in standalone mode">
              <Box sx={{ overflowX: "auto" }}>
                <Box
                  component="table"
                  sx={{
                    width: "100%",
                    borderCollapse: "collapse",
                    "& th": { textAlign: "left", color: tokens.textDim, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700, p: "10px 12px", borderBottom: `1px solid ${tokens.border}`, whiteSpace: "nowrap" },
                    "& td": { p: "12px", borderBottom: `1px solid ${tokens.border}`, fontSize: 13, fontFamily: mono },
                  }}
                >
                  <thead>
                    <tr>
                      <th>Platform</th>
                      <th>Devices</th>
                      <th>Users</th>
                      <th>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {platforms.length === 0 ? (
                      <tr>
                        <td colSpan={4} style={{ textAlign: "center", color: tokens.textDim }}>No installs recorded yet.</td>
                      </tr>
                    ) : (
                      platforms.map((p, i) => {
                        const totalDevices = platforms.reduce((sum, r) => sum + num(r?.devices), 0);
                        return (
                          <tr key={`${p?.platform}-${i}`}>
                            <td style={{ fontFamily: "inherit" }}>{p?.platform || "unknown"}</td>
                            <td>{num(p?.devices).toLocaleString()}</td>
                            <td>{num(p?.users).toLocaleString()}</td>
                            <td style={{ color: tokens.secondary }}>{pct(num(p?.devices), totalDevices).toFixed(1)}%</td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Box>
              </Box>
            </SectionCard>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
